import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Circle, CheckCircle2, Clock, ArrowRight, NotebookPen } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { brand } from '@iheart/shared';
import { cn } from '@iheart/ui';
import { useAuth } from '../../lib/auth';
import { useAllMyTasks, useToggleTaskDone, type BriefTask, type TaskStatus } from '../../lib/projects';
import { useWorkspaces, useWorkspaceStore, type Workspace } from '../../lib/workspace';
import { useDominaBriefStore } from './DominaBriefStore';
import { useChatWidgetStore } from './ChatWidgetStore';

function wsInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

const PRIORITY_RANK: Record<string, number> = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
const PRIORITY_DOT: Record<string, string> = {
  URGENT: 'bg-red-500',
  HIGH: 'bg-orange-400',
  MEDIUM: 'bg-blue-400',
  LOW: 'bg-neutral-300',
};

const CHEEKY_LINES = [
  'Howzit {name}! These tasks won’t sort themselves — let’s get cracking. 💪',
  'Morning {name}! A bit of focus and you’ll knock these out. 🔥',
  'Lekker to see you, {name}! Let’s tackle these one by one. ✅',
  'Yebo {name}, you’ve got this — tick a few off and go treat yourself. 😎',
];

function greetingPrefix(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function dueMs(t: BriefTask): number {
  return t.dueDate ? new Date(t.dueDate).getTime() : Infinity;
}
function urgencyCmp(a: BriefTask, b: BriefTask): number {
  const d = dueMs(a) - dueMs(b);
  if (d !== 0) return d;
  return (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
}

export function DominaBrief() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isExpanded, collapse } = useDominaBriefStore();
  // The chat panel and this brief are mutually exclusive — opening one closes
  // the other so two panels never stack on the right.
  const { isOpen: isChatOpen } = useChatWidgetStore();
  const tasks = useAllMyTasks();
  const toggle = useToggleTaskDone();
  const workspaces = useWorkspaces();
  const setCurrentWs = useWorkspaceStore((s) => s.setCurrent);

  const wsMap = useMemo(
    () => new Map((workspaces.data ?? []).map((w) => [w.publicId, w])),
    [workspaces.data],
  );

  const firstName = user?.name?.split(' ')[0] || 'there';

  function goToWorkspace(publicId: string) {
    const full = qc.getQueryData<Workspace[]>(['workspaces'])?.find((w) => w.publicId === publicId);
    if (full) setCurrentWs(full);
    collapse();
    navigate('/workspace');
  }

  const { groups } = useMemo(() => {
    const all = (tasks.data ?? []).filter((t) => !t.verifiedAt);
    const byWs = new Map<string, { name: string; items: BriefTask[] }>();
    for (const t of all) {
      const g = byWs.get(t.workspacePublicId) ?? { name: t.workspaceName, items: [] };
      g.items.push(t);
      byWs.set(t.workspacePublicId, g);
    }
    const groups = [...byWs.entries()].map(([publicId, g]) => ({ publicId, ...g }));
    for (const g of groups) {
      g.items.sort((a, b) => {
        const ad = a.status === 'DONE' ? 1 : 0;
        const bd = b.status === 'DONE' ? 1 : 0;
        if (ad !== bd) return ad - bd;
        return urgencyCmp(a, b);
      });
    }
    groups.sort((a, b) => urgencyCmp(a.items[0]!, b.items[0]!));
    const outstanding = all.filter((t) => t.status !== 'DONE').length;
    return { groups, outstanding };
  }, [tasks.data]);

  const cheeky = CHEEKY_LINES[new Date().getDate() % CHEEKY_LINES.length]!.replace('{name}', firstName);

  function tickTask(t: BriefTask) {
    const next: TaskStatus = t.status === 'DONE' ? 'TODO' : 'DONE';
    toggle.mutate({ workspacePublicId: t.workspacePublicId, taskId: t.publicId, status: next });
  }

  return (
    <>
      <AnimatePresence>
        {isExpanded && !isChatOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={brand.motion.spring}
          className={
            'fixed z-50 flex flex-col overflow-hidden border border-white/30 bg-white shadow-2xl ' +
            'inset-2 rounded-2xl ' +
            'sm:inset-auto sm:bottom-20 sm:right-3 sm:h-[min(540px,calc(100vh-6rem))] sm:w-[360px] ' +
            'md:bottom-24 md:right-3'
          }
        >
      {/* Header */}
      <div className="flex shrink-0 items-center gap-2 px-3 py-3" style={{ backgroundImage: brand.gradient.primary }}>
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/20">
          <NotebookPen className="size-4 text-white" />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-semibold leading-tight text-white">
            {greetingPrefix()}, {firstName}
          </span>
          <span className="block text-[11px] leading-tight text-white/80">Your daily brief</span>
        </div>
        <button
          type="button"
          onClick={collapse}
          aria-label="Close brief"
          title="Close"
          className="flex size-7 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* Cheeky line */}
      <div className="shrink-0 border-b border-neutral-100 bg-pink-50/50 px-3 py-2 text-[12px] italic text-pink-700">
        {cheeky}
      </div>

      {/* Task list */}
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {tasks.isLoading ? (
          <p className="py-8 text-center text-xs text-neutral-400">Gathering your tasks…</p>
        ) : groups.length === 0 ? (
          <p className="px-2 py-10 text-center text-sm text-neutral-400">
            No outstanding tasks — lekker! Enjoy the calm, chommie. 😎
          </p>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => {
              const ws = wsMap.get(g.publicId);
              return (
              <section key={g.publicId}>
                <div className="mb-1.5 flex items-center gap-2 rounded-lg bg-pink-50 px-2 py-1.5">
                  <span
                    className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md text-[9px] font-bold text-white"
                    style={{ backgroundColor: ws?.logoUrl ? undefined : (ws?.brandColor ?? '#a3a3a3') }}
                  >
                    {ws?.logoUrl ? (
                      <img src={ws.logoUrl} alt="" className="size-full object-cover" />
                    ) : (
                      wsInitials(g.name)
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs font-semibold text-neutral-700">{g.name}</span>
                  <button
                    type="button"
                    onClick={() => goToWorkspace(g.publicId)}
                    className="flex shrink-0 items-center gap-0.5 text-[11px] font-medium text-pink-600 hover:underline"
                  >
                    Go to workspace <ArrowRight className="size-3" />
                  </button>
                </div>
                <ul className="divide-y divide-neutral-100">
                  {g.items.map((t) => {
                    const done = t.status === 'DONE';
                    const overdue = !done && t.dueDate != null && new Date(t.dueDate).getTime() < Date.now();
                    return (
                      <li key={t.publicId} className="flex items-center gap-2.5 py-2">
                        <button
                          type="button"
                          onClick={() => tickTask(t)}
                          aria-label={done ? 'Reopen task' : 'Mark done'}
                          title={done ? 'Reopen' : 'Mark done'}
                          className="shrink-0 rounded-full p-0.5 transition-transform hover:scale-110"
                        >
                          {done ? (
                            <CheckCircle2 className="size-5 text-emerald-500" />
                          ) : (
                            <Circle className="size-5 text-neutral-400" />
                          )}
                        </button>
                        <div className="min-w-0 flex-1">
                          <span
                            className={cn(
                              'block truncate text-sm',
                              done ? 'text-neutral-400 line-through' : 'text-neutral-800',
                            )}
                          >
                            {t.title}
                          </span>
                          <span className="mt-0.5 flex items-center gap-2 text-[11px] text-neutral-400">
                            <span className={cn('inline-block size-1.5 rounded-full', PRIORITY_DOT[t.priority])} />
                            {t.projectName && <span className="truncate">{t.projectName}</span>}
                            {t.dueDate && (
                              <span className={cn('inline-flex items-center gap-1', overdue && 'font-medium text-red-500')}>
                                <Clock className="size-3" />
                                {new Date(t.dueDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                                {overdue && ' · overdue'}
                              </span>
                            )}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
              );
            })}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-neutral-100 px-3 py-2 text-center text-[10px] text-neutral-400">
        Visit each workspace to get started — this list is just your reminder.
      </div>
        </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
